
import { Users, Building, Calendar } from "lucide-react";
import { Card } from "@/components/ui/card";

interface StrategyDetailsProps {
  strategy: {
    name: string;
    performance: string;
    riskLevel: string;
    minInvestment: string;
    since: string;
    exchange: string;
    followers?: number;
    aum?: number | null;
    winRate?: number | null;
    maxDrawdown?: number | null;
    tradesWon?: number | null;
    tradesLost?: number | null;
    description?: string;
    isPremium?: boolean;
    currency?: string;
  };
}

const StrategyDetails = ({ strategy }: StrategyDetailsProps) => {
  // Check if strategy is premium
  const isPro = strategy.isPremium ?? false;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold mb-6 text-[#243949]">Strategy Details</h2>
      
      <Card className="overflow-hidden shadow-md rounded-xl border-gray-200">
        {/* Header section with name */}
        <div className="p-6 bg-[#607D9B] text-white">
          <h3 className="text-2xl font-semibold">{strategy.name}</h3>
        </div>
        
        {/* Investment amount and badge row */}
        <div className="flex justify-between items-center p-4 bg-white border-b border-gray-200">
          <div className="flex flex-col">
            <span className="text-2xl font-bold text-[#0F1B2C]">{strategy.minInvestment}</span>
            <span className="text-gray-500">Investment</span>
          </div>
          
          <div className={`${isPro ? 'bg-[#F6C528] text-[#012846]' : 'bg-[#607D9B] text-white'} px-4 py-1 rounded-full`}>
            <span className="font-bold">{isPro ? 'PREMIUM' : 'FREE'}</span>
          </div>
        </div>
        
        {/* Strategy details */}
        <div className="p-4 space-y-4 bg-white">
          {/* Followers */}
          <div className="flex items-center gap-2 text-[#4A5568]">
            <Users className="w-5 h-5 text-[#4A5568]" />
            <span className="text-lg">
              {strategy.followers || 81} followers
            </span>
          </div>
          
          {/* Performance - show with date */}
          <div className="flex items-center gap-2">
            <span className="font-semibold text-xl text-[#0F1B2C]">
              {strategy.performance}
            </span>
            <div className="flex items-center gap-1 text-[#4A5568]">
              <Calendar className="w-4 h-4" />
              <span>{strategy.since}</span>
            </div>
          </div>
          
          {/* Exchange */}
          <div className="flex items-center gap-2">
            <Building className="w-5 h-5 text-[#4A5568]" />
            <span className="text-lg font-medium text-[#0F1B2C]">
              {strategy.exchange}
            </span>
          </div>
          
          {/* Stats - Win Rate, Max Drawdown, etc. */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 mt-4">
            {strategy.winRate !== null && strategy.winRate !== undefined && (
              <div className="text-lg">
                <span className="text-[#012846] font-bold">{strategy.winRate}% </span>
                <span className="text-gray-500">Win Rate</span>
              </div>
            )}
            
            {strategy.maxDrawdown !== null && strategy.maxDrawdown !== undefined && (
              <div className="text-lg">
                <span className="text-[#012846] font-bold">{strategy.maxDrawdown}% </span>
                <span className="text-gray-500">Max Drawdown</span>
              </div>
            )}
            
            {strategy.tradesWon !== null && strategy.tradesWon !== undefined && (
              <div className="text-lg">
                <span className="text-[#012846] font-bold">{strategy.tradesWon} </span>
                <span className="text-gray-500">Trades Won</span>
              </div>
            )}
            
            {strategy.tradesLost !== null && strategy.tradesLost !== undefined && (
              <div className="text-lg">
                <span className="text-[#012846] font-bold">{strategy.tradesLost} </span>
                <span className="text-gray-500">Trades Lost</span>
              </div>
            )}
            
            {strategy.aum !== null && strategy.aum !== undefined && (
              <div className="text-lg col-span-2">
                <span className="text-[#012846] font-bold">${typeof strategy.aum === 'number' ? strategy.aum.toLocaleString() : strategy.aum} </span>
                <span className="text-gray-500">AUM</span>
              </div>
            )}
          </div>
          
          {/* Description */}
          {strategy.description && (
            <div className="mt-2 text-[#4A5568]">
              <p>
                {strategy.description}
              </p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default StrategyDetails;

